import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { exportQuoteToCSV, exportQuoteToExcel } from '../utils/exportUtils';
import { motion } from 'framer-motion';
import { FadeIn } from '../components/animations';
import { ServiceFactory } from '../services/factory/ServiceFactory';
import { isDemoMode, demoProposalsStore, DemoProposal, demoQuotesStore, ensureDemoSoldQuotes, demoDocumentsStore, seedDemoDocumentsIfEmpty } from '../utils/demo';
import { isFeatureEnabled } from '../config/environment';

interface DocumentInfo {
  filename: string;
  s3Key: string;
  size: number;
  lastModified: string;
  downloadUrl: string;
  isPdf?: boolean;
}

interface NoteInfo {
  id: string;
  author: string;
  authorRole: string;
  content: string;
  timestamp: string;
  isInternal?: boolean; // For admin-only notes
}

interface QuoteDetailsData {
  submissionId: string;
  companyName: string;
  transperraRep: string;
  ichraEffectiveDate: string;
  pepm: string;
  status: string;
  brokerName: string;
  employerName: string;
  brokerId: string;
  employerId: string;
  agency?: string;
  [key: string]: any;
  documents?: DocumentInfo[];
  notes?: NoteInfo[];
}

const API_KEY = '4ws9KDIWIW11u8mNVP0Th2bGN3GhlnnZlquHiv8b';
// Update with your new REST API Gateway endpoint
const API_URL = process.env.REACT_APP_STAGING_API_URL!;
// API path prefix should match what we created in the REST API
const API_PATH_PREFIX = '/api/quotes';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const QuoteDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useQuery();
  const brokerId = query.get('brokerId') || '';
  const employerId = query.get('employerId') || '';
  const freshSubmission = query.get('freshSubmission') === 'true';
  const { getIdToken, user } = useAuth();
  const [quote, setQuote] = useState<QuoteDetailsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [proposals, setProposals] = useState<DemoProposal[]>([]);
  const [isGeneratingProposal, setIsGeneratingProposal] = useState(false);
  const [proposalTimer, setProposalTimer] = useState<NodeJS.Timeout | null>(null);
  const demoMode = isDemoMode();

  // Notes state management
  const [newNote, setNewNote] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submittingNote, setSubmittingNote] = useState(false);
  
  // Check if user can add notes (TPA admin/user or Kyndly admin)
  const canAddNotes = user?.role === 'admin' || user?.role === 'kyndly_staff' || 
                     user?.role === 'tpa_admin' || user?.role === 'tpa_user';

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        setIsLoading(true);

        // Use ServiceFactory instead of direct API calls
        const apiService = ServiceFactory.createApiService();
        const data = await apiService.getQuoteById(id!, brokerId, employerId);

        if (data) {
          // Load notes from documents if they exist
          if (data.documents && data.documents.length > 0) {
            const notes = await loadNotesFromDocuments(data.documents);
            data.notes = notes;
          }

          setQuote(data);

          // Load proposals in demo mode
          if (demoMode && data?.submissionId) {
            const existingProposals = demoProposalsStore.listBySubmission(data.submissionId);

            // Auto-generation simulation for Legacy Metals
            const shouldShowGeneration =
              data.companyName === 'Legacy Metals' &&
              data.proposalGenerated !== true &&
              freshSubmission &&
              existingProposals.length === 0;

            if (shouldShowGeneration) {
              setIsGeneratingProposal(true);
              setProposals([]);

              const timer = setTimeout(() => {
                const seeded = demoProposalsStore.ensureSeedForSubmission(
                  data.submissionId,
                  data.companyName || 'Employer'
                );
                setProposals(seeded);
                setIsGeneratingProposal(false);

                // Mark as generated to prevent future simulations
                demoQuotesStore.updateProposalGenerated(data.submissionId, true);

                // Clean up URL
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete('freshSubmission');
                window.history.replaceState({}, '', newUrl.toString());
              }, 15000);

              setProposalTimer(timer);
            } else {
              // Load existing proposals
              const seeded = demoProposalsStore.ensureSeedForSubmission(
                data.submissionId,
                data.companyName || 'Employer'
              );
              setProposals(seeded);
            }
          }

          setError(null);
        } else {
          setError('Quote not found');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError(`Failed to fetch quote details: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setIsLoading(false);
      }
    };
    if (id) fetchQuote();
  }, [id, brokerId, employerId, getIdToken]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (proposalTimer) {
        clearTimeout(proposalTimer);
      }
    };
  }, [proposalTimer]);

  const handleUploadClick = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!quote || !e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setUploading(true);
    try {
      const token = await getIdToken();
      
      const formData = new FormData();
      formData.append('file', file);
      const url = `${API_URL}${API_PATH_PREFIX}/${quote.submissionId}/documents?brokerId=${quote.brokerId}&employerId=${quote.employerId}`;
      console.log('Making upload request to:', url);
      
      // Important: Don't set Content-Type header for multipart/form-data
      // The browser will set it automatically with the correct boundary
      const headers: any = {
        'x-api-key': API_KEY
      };
      
      // Only add Authorization header if the token is valid
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        // Add mode and credentials for better CORS support
        mode: 'cors',
        credentials: 'include'
      });
      
      console.log('Upload response status:', response.status);
      
      if (response.ok) {
        alert('Document uploaded successfully!');
        // Refresh quote details to show new document
        window.location.reload();
      } else {
        const errorText = await response.text();
        console.error('Upload error response:', response.status, errorText);
        alert(`Failed to upload document: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Error uploading document.');
    } finally {
      setUploading(false);
    }
  };

  const handleNoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quote || !newNote.trim() || !user) return;
    
    setSubmittingNote(true);
    try {
      const token = await getIdToken();
      
      const noteData = {
        id: `note-${Date.now()}`,
        author: user.name || user.email || user.username,
        authorRole: user.role,
        content: newNote.trim(),
        timestamp: new Date().toISOString(),
        isInternal: isInternal
      };
      
      // Create a JSON file blob for the note
      const noteBlob = new Blob([JSON.stringify(noteData, null, 2)], {
        type: 'application/json'
      });
      
      // Create a File object with a descriptive filename
      const noteFile = new File([noteBlob], `note-${Date.now()}.json`, {
        type: 'application/json'
      });
      
      // Use the existing documents endpoint
      const formData = new FormData();
      formData.append('file', noteFile);
      const url = `${API_URL}${API_PATH_PREFIX}/${quote.submissionId}/documents?brokerId=${quote.brokerId}&employerId=${quote.employerId}`;
      console.log('Making note upload request to documents endpoint:', url);
      
      const headers: any = {
        'x-api-key': API_KEY
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        mode: 'cors',
        credentials: 'include'
      });
      
      console.log('Note upload response status:', response.status);
      
      if (response.ok) {
        // Add the new note to the current quote state
        setQuote(prev => prev ? {
          ...prev,
          notes: [...(prev.notes || []), noteData]
        } : null);
        
        // Clear the form
        setNewNote('');
        setIsInternal(false);
        
        alert('Note added successfully!');
      } else {
        const errorText = await response.text();
        console.error('Note upload error response:', response.status, errorText);
        alert(`Failed to add note: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      console.error('Note upload error:', err);
      alert('Error adding note.');
    } finally {
      setSubmittingNote(false);
    }
  };

  // Function to handle exporting the quote to CSV
  const handleExportToCsv = () => {
    if (quote) {
      exportQuoteToCSV(quote);
      setShowExportMenu(false);
    }
  };

  // Function to handle exporting the quote to Excel
  const handleExportToExcel = () => {
    if (quote) {
      exportQuoteToExcel(quote);
      setShowExportMenu(false);
    }
  };

  // Close the export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showExportMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('#export-menu') && !target.closest('#export-button')) {
          setShowExportMenu(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  // Function to load notes from documents
  const loadNotesFromDocuments = async (documents: DocumentInfo[]) => {
    const noteFiles = documents.filter(doc => 
      doc.filename.startsWith('note-') && doc.filename.endsWith('.json')
    );
    
    const notes: NoteInfo[] = [];
    
    for (const noteFile of noteFiles) {
      try {
        const response = await fetch(noteFile.downloadUrl);
        if (response.ok) {
          const noteData = await response.json();
          notes.push(noteData);
        }
      } catch (error) {
        console.error('Error loading note file:', noteFile.filename, error);
      }
    }
    
    return notes;
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 py-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-brand-gradient rounded-brand p-6 mb-8 text-white shadow-brand"
      >
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">Quote Details</h1>
            <p className="text-sky-100">View and manage quote information</p>
          </div>
          {quote && (
            <div className="relative">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                id="export-button"
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="bg-white hover:bg-gray-100 text-seafoam font-medium px-4 py-2 rounded-md text-sm transition-colors duration-200 flex items-center shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Export Quote
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </motion.button>
              
              {showExportMenu && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  id="export-menu"
                  className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10"
                >
                  <div className="py-1">
                    <motion.button
                      whileHover={{ backgroundColor: "#f3f4f6" }}
                      onClick={handleExportToCsv}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <svg className="h-4 w-4 mr-2 text-gray-500" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                      </svg>
                      Export as CSV
                    </motion.button>
                    <motion.button
                      whileHover={{ backgroundColor: "#f3f4f6" }}
                      onClick={handleExportToExcel}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <svg className="h-4 w-4 mr-2 text-green-600" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                      </svg>
                      Export as Excel
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </motion.div>
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-seafoam border-t-transparent"></div>
        </div>
      ) : error ? (
        <FadeIn>
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">{error}</div>
        </FadeIn>
      ) : quote ? (
        <div className="space-y-6">
          {/* Proposals Section */}
          {isFeatureEnabled('implementationForm') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.2 }}
              className="bg-white rounded-brand shadow-brand overflow-hidden p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-seafoam">Proposals</h2>
              </div>
              {isGeneratingProposal ? (
                <div className="border rounded-md overflow-hidden overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-80">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="pointer-events-none bg-blue-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                          <div className="flex items-center gap-3">
                            <svg className="animate-spin h-4 w-4 text-seafoam" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-seafoam font-medium">Generating Proposal...</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Processing</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Kyndly AI</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium min-w-80">
                          <div className="flex gap-2">
                            <span className="bg-gray-300 text-gray-500 px-3 py-1 rounded-md text-sm cursor-not-allowed">
                              Please wait...
                            </span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : proposals && proposals.length > 0 ? (
                <div className="border rounded-md overflow-hidden overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-80">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {proposals.map((p) => (
                        <tr
                          key={p.id}
                          className={`${p.url ? 'cursor-pointer hover:bg-gray-50' : ''} transition-colors duration-200`}
                          onClick={() => p.url && window.open(p.url, '_blank', 'noopener,noreferrer')}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{p.title}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(p.createdAt).toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{p.author}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium min-w-80">
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/implementation/${id}?brokerId=${brokerId}&employerId=${employerId}`);
                                }}
                                className="bg-seafoam hover:bg-seafoam-600 text-white px-3 py-1 rounded-md text-sm transition-colors duration-200"
                              >
                                Implementation Form
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-md">
                  <p className="mt-2 text-sm text-gray-500">There are no proposals for this quote yet</p>
                </div>
              )}
            </motion.div>
          )}

          {/* Basic Quote Info Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.2 }}
            className="bg-white rounded-brand shadow-brand overflow-hidden p-6"
          >
            <h2 className="text-xl font-semibold mb-4 text-seafoam">Quote Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Company</h3>
                  <p className="text-lg font-medium">{quote.companyName}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">ICHRA Effective Date</h3>
                  <p className="text-lg">{quote.ichraEffectiveDate}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">PEPM</h3>
                  <p className="text-lg">${quote.pepm}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Current Funding Strategy</h3>
                  <p className="text-lg">{quote.currentFundingStrategy || 'N/A'}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Representative</h3>
                  <p className="text-lg">{quote.transperraRep}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Status</h3>
                  <p>
                    <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${
                      quote.status === 'Approved'
                        ? 'bg-green-100 text-green-800'
                        : quote.status === 'Pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {quote.status || 'New'}
                    </span>
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Submission ID</h3>
                  <p className="text-xs font-mono bg-gray-100 py-1 px-2 rounded inline-block">{quote.submissionId}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Submission Date</h3>
                  <p className="text-lg">{new Date(quote.submissionDate || Date.now()).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Plan & Company Details */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.2 }}
            className="bg-white rounded-brand shadow-brand overflow-hidden p-6"
          >
            <h2 className="text-xl font-semibold mb-4 text-seafoam">Plan Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Target Deductible</h3>
                  <p className="text-lg">{quote.targetDeductible ? `$${quote.targetDeductible}` : 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Target HSA</h3>
                  <p className="text-lg">{quote.targetHSA || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Is GLI</h3>
                  <p className="text-lg">{quote.isGLI ? 'Yes' : 'No'}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Priority Level</h3>
                  <p className="text-lg capitalize">{quote.priorityLevel || 'Standard'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Contact Type</h3>
                  <p className="text-lg">{quote.contactType || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Additional Notes</h3>
                  <p className="text-lg italic">{quote.additionalNotes || 'No additional notes'}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Broker & Employer Info */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.2 }}
            className="bg-white rounded-brand shadow-brand overflow-hidden p-6"
          >
            <h2 className="text-xl font-semibold mb-4 text-seafoam">Broker & Employer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Broker Name</h3>
                  <p className="text-lg">{quote.brokerName || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Broker Email</h3>
                  <p className="text-lg">
                    {quote.brokerEmail ? (
                      <a href={`mailto:${quote.brokerEmail}`} className="text-seafoam hover:underline">
                        {quote.brokerEmail}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Broker ID</h3>
                  <p className="text-sm font-mono bg-gray-100 py-1 px-2 rounded inline-block">{quote.brokerId}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Agency</h3>
                  <p className="text-lg">{quote.agency || 'N/A'}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Employer Name</h3>
                  <p className="text-lg">{quote.employerName || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Employer ID</h3>
                  <p className="text-sm font-mono bg-gray-100 py-1 px-2 rounded inline-block">{quote.employerId}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">TPA ID</h3>
                  <p className="text-sm font-mono bg-gray-100 py-1 px-2 rounded inline-block">{quote.tpaId}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Documents Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.2 }}
            className="bg-white rounded-brand shadow-brand overflow-hidden p-6"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-seafoam">Documents</h2>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <button
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors duration-300 flex items-center gap-2"
                onClick={handleUploadClick}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Upload Document</span>
                  </>
                )}
              </button>
            </div>
            
            {quote.documents && quote.documents.filter(doc => !(doc.filename.startsWith('note-') && doc.filename.endsWith('.json'))).length > 0 ? (
              <div className="border rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Upload Date</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {quote.documents
                      .filter(doc => !(doc.filename.startsWith('note-') && doc.filename.endsWith('.json')))
                      .map((doc) => (
                      <tr key={doc.s3Key}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{doc.filename}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(doc.size / 1024).toFixed(1)} KB</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(doc.lastModified).toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <a
                              href={doc.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-seafoam hover:bg-seafoam-600 text-white px-3 py-1 rounded-md text-sm transition-colors duration-200 flex items-center"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                              </svg>
                              View
                            </a>
                            <a
                              href={doc.downloadUrl}
                              download
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm transition-colors duration-200 flex items-center"
                              onClick={(e) => {
                                // Force download by recreating the link and clicking it programmatically
                                e.preventDefault();
                                const a = document.createElement('a');
                                a.href = doc.downloadUrl;
                                a.download = doc.filename || 'document';
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              Download
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">No documents uploaded yet</p>
                <p className="text-xs text-gray-400 mt-1">Click the upload button to add a document</p>
              </div>
            )}
          </motion.div>

          {/* Notes & Communication Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.2 }}
            className="bg-white rounded-brand shadow-brand overflow-hidden p-6"
          >
            <h2 className="text-xl font-semibold mb-4 text-seafoam">Notes & Communication</h2>
            
            {/* Add Note Form - Only for authorized users */}
            {canAddNotes && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium mb-3 text-gray-800">Add Note</h3>
                <form onSubmit={handleNoteSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="noteContent" className="block text-sm font-medium text-gray-700 mb-2">
                      Note Content
                    </label>
                    <textarea
                      id="noteContent"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Enter your note or update here..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-seafoam focus:border-transparent resize-vertical"
                      rows={4}
                      required
                    />
                  </div>
                  
                  {/* Internal note checkbox - Only for admins */}
                  {(user?.role === 'admin' || user?.role === 'kyndly_staff') && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isInternal"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="h-4 w-4 text-seafoam focus:ring-seafoam border-gray-300 rounded"
                      />
                      <label htmlFor="isInternal" className="ml-2 block text-sm text-gray-700">
                        Internal note (visible only to Kyndly staff)
                      </label>
                    </div>
                  )}
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={submittingNote || !newNote.trim()}
                      className="px-4 py-2 bg-seafoam hover:bg-seafoam-600 text-white rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {submittingNote ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Adding Note...</span>
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span>Add Note</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
            
            {/* Display Existing Notes */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-800">Communication History</h3>
              
              {quote.notes && quote.notes.length > 0 ? (
                <div className="space-y-3">
                  {quote.notes
                    .filter(note => {
                      // Filter internal notes based on user role
                      if (note.isInternal) {
                        return user?.role === 'admin' || user?.role === 'kyndly_staff';
                      }
                      return true;
                    })
                    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                    .map((note) => (
                      <div 
                        key={note.id} 
                        className={`p-4 rounded-lg border-l-4 ${
                          note.isInternal 
                            ? 'bg-red-50 border-l-red-400' 
                            : 'bg-blue-50 border-l-blue-400'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{note.author}</span>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              note.authorRole === 'admin' || note.authorRole === 'kyndly_staff'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {note.authorRole.replace('_', ' ').toUpperCase()}
                            </span>
                            {note.isInternal && (
                              <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                                INTERNAL
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500">
                            {new Date(note.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-4.906-1.456L3 21l2.544-5.094A8.959 8.959 0 013 12c0-4.418 3.582-8 8-8s8 3.582 8 8z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">No notes or communication yet</p>
                  {canAddNotes && (
                    <p className="text-xs text-gray-400 mt-1">Add the first note to start the conversation</p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      ) : null}
    </div>
  );
};

const S3_BUCKET = 'kyndly-ichra-documents';

export default QuoteDetails; 